import { useState } from "react";
import { toast } from "sonner";
import { Youtube, Upload, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { uploadFile } from "../FileField";
import { findFull } from "./QueueRow";
import { newTrack, fileTrack } from "./trackBits";

const btn = "shrink-0 flex items-center gap-1.5 px-3 py-2 border font-mono text-[10px] font-bold tracking-[0.2em] disabled:opacity-50 transition-colors";

// Ways to give a section a full-length song: paste a YouTube link, upload audio, or upgrade a Spotify preview.
export const FullSongActions = ({ id, current, onPick }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(null);
  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  const fromYouTube = () => run("yt", async () => {
    if (!/youtu/.test(url)) throw new Error("Paste a YouTube or YouTube Music song link.");
    const { data } = await api.get("/music/source", { params: { url } });
    onPick(newTrack(data.tracks[0]));
    setUrl("");
  });
  const fromFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) { toast.error("Audio files can be up to 200MB."); return; }
    run("file", async () => onPick(fileTrack((await uploadFile(f, () => {})).url, f.name)));
  };
  const upgrade = () => run("full", async () => onPick({ ...current, ...(await findFull(current)) }));
  const preview = current?.source === "spotify" && !current?.video_id;

  return (
    <div className="flex flex-wrap items-center gap-2 pl-0 sm:pl-[136px]">
      {current && (
        <span data-testid={`section-track-status-${id}`} className={`font-mono text-[10px] tracking-[0.2em] ${preview ? "text-zinc-400" : "text-[#F5A524]"}`}>
          {preview ? "30S PREVIEW" : <span className="flex items-center gap-1"><Check size={11} /> FULL SONG</span>}
        </span>
      )}
      {preview && (
        <button type="button" data-testid={`section-track-full-${id}`} onClick={upgrade} disabled={!!busy} className={`${btn} border-[#F5A524]/50 text-[#F5A524] hover:bg-[#F5A524] hover:text-black`}>
          <Youtube size={12} /> {busy === "full" ? "SEARCHING..." : "PLAY FULL VERSION"}
        </button>
      )}
      <input data-testid={`section-track-yt-input-${id}`} value={url} onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fromYouTube(); } }}
        placeholder="Paste a YouTube song link" className="min-w-0 flex-1 basis-48 bg-[#0B0C10] border border-white/10 focus:border-[#F5A524] outline-none px-3 py-2 text-xs" />
      <button type="button" data-testid={`section-track-yt-set-${id}`} onClick={fromYouTube} disabled={!!busy || !url.trim()} className={`${btn} border-[#FF2A55]/60 text-[#FF2A55] hover:bg-[#FF2A55] hover:text-black`}>
        <Youtube size={12} /> {busy === "yt" ? "LOADING..." : "USE LINK"}
      </button>
      <label data-testid={`section-track-upload-${id}`} className={`${btn} cursor-pointer border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 ${busy ? "pointer-events-none opacity-50" : ""}`}>
        <Upload size={12} /> {busy === "file" ? "UPLOADING..." : "UPLOAD AUDIO"}
        <input data-testid={`section-track-upload-input-${id}`} type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,audio/*" className="hidden" onChange={fromFile} />
      </label>
    </div>
  );
};
